import React, { useEffect, useState } from "react";
import { Modal, Button, Form, ListGroup } from "react-bootstrap";
import { updateDoc, setDoc, doc } from "firebase/firestore";
import { db } from "../../../firebase";
import "./NodeList.css";
import AvailabilityField from "./AvailabilityField";
import { getAvailabilityStatus } from "../../../utils/nodeAvailability";
import { validGoogleMapsUrl } from "./customerLocationUtils";

const EditCustomerModal = ({
  show,
  onHide,
  customer,
  nodeId,
  node,
  onUpdate,
  onCreate,
  isCreateMode,
}) => {
  const [customerNameTH, setCustomerNameTH] = useState("");
  const [customerNameEN, setCustomerNameEN] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [googleMapsUrl, setGoogleMapsUrl] = useState("");
  const [locationError, setLocationError] = useState("");
  const [type, setType] = useState("Hospital");
  const [hospitalSleepAvailable, setHospitalSleepAvailable] = useState("");
  const [homeSleepAvailable, setHomeSleepAvailable] = useState("");
  const [opdDoctorVisitAvailable, setOpdDoctorVisitAvailable] = useState("");
  const [hospitalRoomCount, setHospitalRoomCount] = useState("");
  const [roomCountError, setRoomCountError] = useState("");
  const [availabilityStatus, setAvailabilityStatus] = useState("available");
  const [emailList, setEmailList] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [editEmail, setEditEmail] = useState("");
  const nodeUnavailable = getAvailabilityStatus(node) === "unavailable";

  useEffect(() => {
    if (show) {
      setLocationError("");
      setRoomCountError("");
      if (isCreateMode) {
        setCustomerNameTH("");
        setCustomerNameEN("");
        setLocationAddress("");
        setGoogleMapsUrl("");
        setType("Hospital");
        setHospitalSleepAvailable("no");
        setHomeSleepAvailable("no");
        setOpdDoctorVisitAvailable("no");
        setHospitalRoomCount("");
        setAvailabilityStatus("available");
        setEmailList([]);
        setNewEmail("");
        setEditIndex(null);
        setEditEmail("");
      } else if (customer) {
        setCustomerNameTH(customer.CustomerNameTH || "");
        setCustomerNameEN(customer.CustomerNameEN || "");
        setLocationAddress(customer.locationAddress || "");
        setGoogleMapsUrl(customer.googleMapsUrl || "");
        setType(customer.Type || "Hospital");
        setHospitalSleepAvailable(
          typeof customer.hospitalSleepAvailable === "boolean"
            ? (customer.hospitalSleepAvailable ? "yes" : "no")
            : "",
        );
        setHomeSleepAvailable(
          typeof customer.homeSleepAvailable === "boolean"
            ? (customer.homeSleepAvailable ? "yes" : "no")
            : "",
        );
        setOpdDoctorVisitAvailable(
          typeof customer.opdDoctorVisitAvailable === "boolean"
            ? (customer.opdDoctorVisitAvailable ? "yes" : "no")
            : "",
        );
        setHospitalRoomCount(
          customer.hospitalSleepAvailable === true && customer.hospitalRoomCount != null
            ? String(customer.hospitalRoomCount)
            : "",
        );
        setAvailabilityStatus(getAvailabilityStatus(customer));
        setEmailList(customer.emailList || []);
        setNewEmail("");
        setEditIndex(null);
        setEditEmail("");
      }
    }
  }, [show, customer, isCreateMode]);

  const makeCustomerDocId = (nameEN) => {
    return nameEN.trim().replace(/\//g, "-").replace(/\s+/g, " ");
  };

  const handleSave = async () => {
    const trimmedCustomerNameTH = customerNameTH.trim();
    const trimmedCustomerNameEN = customerNameEN.trim();

    if (!trimmedCustomerNameTH) {
      alert("Please enter Customer Name (TH)");
      return;
    }

    if (!trimmedCustomerNameEN) {
      alert("Please enter Customer Name (EN)");
      return;
    }

    const customerDocId = makeCustomerDocId(trimmedCustomerNameEN);
    const mapsUrl = validGoogleMapsUrl(googleMapsUrl);
    if (googleMapsUrl.trim() && !mapsUrl) {
      setLocationError("Please enter a valid HTTPS Google Maps link.");
      return;
    }
    setLocationError("");

    const roomCount = hospitalRoomCount.trim() === "" ? null : Number(hospitalRoomCount);
    if (hospitalSleepAvailable === "yes" && roomCount !== null && (!Number.isSafeInteger(roomCount) || roomCount < 1)) {
      setRoomCountError("Please enter a whole number of hospital rooms greater than zero.");
      return;
    }
    setRoomCountError("");

    const updatedData = {
      CustomerNameTH: trimmedCustomerNameTH,
      CustomerNameEN: trimmedCustomerNameEN,
      locationAddress: locationAddress.trim(),
      googleMapsUrl: mapsUrl,
      Type: type,
      hospitalSleepAvailable: hospitalSleepAvailable === "" ? null : hospitalSleepAvailable === "yes",
      homeSleepAvailable: homeSleepAvailable === "" ? null : homeSleepAvailable === "yes",
      opdDoctorVisitAvailable: opdDoctorVisitAvailable === "" ? null : opdDoctorVisitAvailable === "yes",
      hospitalRoomCount: hospitalSleepAvailable === "yes" ? roomCount : null,
      emailList,
      // Keep the customer's own setting while the node overrides its display.
      availabilityStatus,
    };

    try {
      if (isCreateMode) {
        const customerRef = doc(
          db,
          "nodes",
          nodeId,
          "customers",
          customerDocId,
        );

        await setDoc(customerRef, updatedData);

        if (onCreate) {
          onCreate({
            ...updatedData,
            id: customerDocId,
          });
        }
      } else {
        const customerRef = doc(db, "nodes", nodeId, "customers", customer.id);

        await updateDoc(customerRef, updatedData);

        if (onUpdate) {
          onUpdate(customer.id, updatedData);
        }
      }

      onHide();
    } catch (error) {
      console.error("Error saving customer:", error);
      alert("Save customer failed");
    }
  };

  const handleAddEmail = () => {
    const trimmed = newEmail.trim();

    if (!trimmed) return;

    if (emailList.includes(trimmed)) {
      setNewEmail("");
      return;
    }

    setEmailList([...emailList, trimmed]);
    setNewEmail("");
  };

  const handleEditEmail = () => {
    const trimmed = editEmail.trim();

    if (editIndex === null || !trimmed) return;

    const duplicatedIndex = emailList.findIndex(
      (email, index) => email === trimmed && index !== editIndex,
    );

    if (duplicatedIndex !== -1) return;

    const updatedEmailList = emailList.map((email, index) =>
      index === editIndex ? trimmed : email,
    );

    setEmailList(updatedEmailList);
    setEditIndex(null);
    setEditEmail("");
  };

  const handleDeleteEmail = (index) => {
    const updatedEmailList = emailList.filter((_, i) => i !== index);
    setEmailList(updatedEmailList);

    if (editIndex === index) {
      setEditIndex(null);
      setEditEmail("");
    }
  };

  const handleCancelEditEmail = () => {
    setEditIndex(null);
    setEditEmail("");
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable className="node-editor">
      <Modal.Header closeButton>
        <Modal.Title>
          {isCreateMode ? "Add New Customer" : "Edit Customer"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group controlId="node-customer-name-th">
            <Form.Label>Customer Name (TH)</Form.Label>
            <Form.Control
              type="text"
              value={customerNameTH}
              onChange={(e) => setCustomerNameTH(e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="node-customer-name-en">
            <Form.Label>Customer Name (EN)</Form.Label>
            <Form.Control
              type="text"
              value={customerNameEN}
              onChange={(e) => setCustomerNameEN(e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="node-customer-type">
            <Form.Label>Type</Form.Label>
            <Form.Control
              as="select"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="Hospital">Hospital</option>
              <option value="Clinic">Clinic</option>
            </Form.Control>
          </Form.Group>

          <Form.Row>
            <Form.Group controlId="node-customer-hospital-sleep" className="col-sm-4">
              <Form.Label>Sleep in Hospital</Form.Label>
              <Form.Control
                as="select"
                value={hospitalSleepAvailable}
                onChange={(e) => {
                  setHospitalSleepAvailable(e.target.value);
                  if (e.target.value !== "yes") setHospitalRoomCount("");
                  setRoomCountError("");
                }}
              >
                <option value="">Not set</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="node-customer-home-sleep" className="col-sm-4">
              <Form.Label>Home Sleep</Form.Label>
              <Form.Control
                as="select"
                value={homeSleepAvailable}
                onChange={(e) => setHomeSleepAvailable(e.target.value)}
              >
                <option value="">Not set</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="node-customer-opd-doctor-visit" className="col-sm-4">
              <Form.Label>OPD Doctor Visit</Form.Label>
              <Form.Control
                as="select"
                value={opdDoctorVisitAvailable}
                onChange={(e) => setOpdDoctorVisitAvailable(e.target.value)}
              >
                <option value="">Not set</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Form.Control>
            </Form.Group>
          </Form.Row>

          {hospitalSleepAvailable === "yes" && (
            <Form.Group controlId="node-customer-hospital-rooms">
              <Form.Label>Hospital Room Count</Form.Label>
              <Form.Control
                type="number"
                min="1"
                step="1"
                value={hospitalRoomCount}
                isInvalid={Boolean(roomCountError)}
                aria-describedby={`node-customer-hospital-rooms-help${roomCountError ? " node-customer-hospital-rooms-error" : ""}`}
                onChange={(e) => {
                  setHospitalRoomCount(e.target.value);
                  setRoomCountError("");
                }}
              />
              <Form.Text id="node-customer-hospital-rooms-help" className="text-muted">Optional. Leave blank when rooms are shared across the node.</Form.Text>
              {roomCountError && <div id="node-customer-hospital-rooms-error" role="alert" className="text-danger small mt-1">{roomCountError}</div>}
            </Form.Group>
          )}

          <AvailabilityField
            id="node-customer-availability"
            value={nodeUnavailable ? "unavailable" : availabilityStatus}
            inherited={nodeUnavailable}
            onChange={(event) => setAvailabilityStatus(event.target.value)}
          />

          <Form.Group controlId="node-customer-address">
            <Form.Label>Location Address</Form.Label>
            <Form.Control as="textarea" rows={2} value={locationAddress} onChange={(e) => setLocationAddress(e.target.value)} />
          </Form.Group>

          <Form.Group controlId="node-customer-maps">
            <Form.Label>Google Maps Link</Form.Label>
            <Form.Control type="url" value={googleMapsUrl} placeholder="https://maps.app.goo.gl/..." isInvalid={Boolean(locationError)} aria-describedby="node-customer-maps-help" onChange={(e) => { setGoogleMapsUrl(e.target.value); setLocationError(""); }} />
            {locationError && <div role="alert" className="text-danger small mt-1">{locationError}</div>}
            <Form.Text id="node-customer-maps-help" className="text-muted">Optional. Leave blank to search Google Maps using the customer name and address.</Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label htmlFor="node-customer-email">Email List</Form.Label>

            <ListGroup>
              {emailList.length > 0 ? (
                emailList.map((email, index) => (
                  <ListGroup.Item key={index}>
                    {editIndex === index ? (
                      <div className="d-flex">
                        <Form.Control
                          type="text"
                          aria-label="Edit email"
                          value={editEmail}
                          onChange={(e) => setEditEmail(e.target.value)}
                        />
                        <Button
                          variant="success"
                          size="sm"
                          onClick={handleEditEmail}
                          className="ml-2"
                        >
                          Save
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleCancelEditEmail}
                          className="ml-2"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex justify-content-between align-items-center">
                        <span>{email}</span>
                        <div>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => {
                              setEditIndex(index);
                              setEditEmail(email);
                            }}
                            className="ml-2"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => handleDeleteEmail(index)}
                            className="ml-2 text-danger"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </ListGroup.Item>
                ))
              ) : (
                <ListGroup.Item className="text-muted">
                  No email added
                </ListGroup.Item>
              )}
            </ListGroup>

            <div className="d-flex mt-2">
              <Form.Control
                type="text"
                id="node-customer-email"
                placeholder="Add new email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddEmail();
                  }
                }}
              />
              <Button
                variant="primary"
                onClick={handleAddEmail}
                className="ml-2"
              >
                Add
              </Button>
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {isCreateMode ? "Create" : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditCustomerModal;
